import { type ElementType } from 'react';
import Link from 'next/link';
import { Chip, ChipsGroup } from '@johnlewispartnership/wtr-ingredients/ingredients/Chips';
import { Typography } from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { clsx } from 'clsx';
import CountryFlagChip from '@/components/CountryFlagChip/CountryFlagChip';
import { type Product } from '@/services/products/types';
import {
  NONE_RED_WINE_STYLES_LABELS,
  RED_WINE_STYLES_LABELS,
} from '@/components/constants/product';
import styles from '../ProductPod.module.scss';

export function ProductPodHeader({
  onRedirect,
  product,
  titleElement,
  titleId,
}: {
  product: Product;
  titleElement: ElementType;
  onRedirect: () => void;
  titleId?: string | null;
}) {
  const { country, name, origin, slug, summary, wineStyle } = product;
  const wineStyleLabel =
    (wineStyle && { ...RED_WINE_STYLES_LABELS, ...NONE_RED_WINE_STYLES_LABELS }[wineStyle]) ?? '';
  return (
    <>
      <div className={styles.nameAndSummary}>
        {titleId === null ? (
          <Link href={{ pathname: '/products/[slug]', query: { slug } }} onClick={onRedirect}>
            <Typography
              data-testid="plp-product-title"
              element={titleElement}
              noMargins
              styleAs="paragraphHeading"
              textStyle="heading-l"
            >
              {name}
            </Typography>
          </Link>
        ) : (
          <Link
            data-testid="plp-product-title"
            href={{ pathname: '/products/[slug]', query: { slug } }}
            id={titleId}
            onClick={onRedirect}
          >
            <Typography
              element={titleElement}
              noMargins
              styleAs="paragraphHeading"
              textStyle="heading-l"
            >
              {name}
            </Typography>
          </Link>
        )}

        {summary ? (
          <Link
            className={styles.linkWithoutUnderline}
            href={{ pathname: '/products/[slug]', query: { slug } }}
            onClick={onRedirect}
          >
            <Typography
              data-testid="pp-product-summary"
              element="span"
              noMargins
              styleAs="paragraph"
              textStyle="body-m"
            >
              &ldquo;{summary}&rdquo;
            </Typography>
          </Link>
        ) : undefined}
      </div>
      <ChipsGroup className={clsx(styles.chipsGroup, styles.chipsLetterSpacing)}>
        {country ? (
          <CountryFlagChip countryOfOrigin={country} />
        ) : (
          origin && (
            <Chip
              className={clsx(styles.chip, styles.chipLetterSpacing, styles.regionOfOriginChip)}
              data-testid="trolley-item-chip-regionOfOrigin"
              small
            >
              {origin}
            </Chip>
          )
        )}
        {wineStyleLabel ? (
          <Chip
            className={clsx(styles.chip, styles.chipLetterSpacing)}
            colour="grey"
            data-testid="pp-product-body"
            small
          >
            {wineStyleLabel}
          </Chip>
        ) : undefined}
      </ChipsGroup>
    </>
  );
}
